"use client";

import type { ReactNode, SelectHTMLAttributes, InputHTMLAttributes } from "react";
import { WorkingLabel } from "@/components/counter/WorkingLabel";
import { useId } from "react";
import { cn } from "@/lib/utils";

/**
 * Form controls for the counter.
 *
 * Sized for a finger on a tablet, not a mouse: 44px minimum control height, generous hit areas,
 * labels always visible (a placeholder-only field is unreadable once filled, and a cashier
 * re-reads a half-typed form constantly).
 *
 * Errors sit under the field at helper-text scale in error ink — the same rule the rest of the
 * product follows. No filled red boxes.
 */

export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: (id: string) => ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <label htmlFor={id} className="text-sm font-bold uppercase tracking-[0.07em] text-text-disabled">
        {label}
        {required && <span className="ml-1 text-text-error">*</span>}
      </label>
      {children(id)}
      {error ? (
        <p className="text-sm font-semibold text-text-error">{error}</p>
      ) : hint ? (
        <p className="text-sm text-text-disabled">{hint}</p>
      ) : null}
    </div>
  );
}

const controlBase =
  "min-h-target-dense w-full rounded-control border bg-bg-base px-3 text-base text-text-strong outline-none " +
  "placeholder:text-text-disabled focus:border-stroke-brand";

export function TextInput({
  invalid,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return (
    <input
      {...props}
      aria-invalid={invalid || undefined}
      className={cn(controlBase, invalid ? "border-stroke-error" : "border-stroke-strong", className)}
    />
  );
}

export function SelectInput({
  invalid,
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
  return (
    <select
      {...props}
      aria-invalid={invalid || undefined}
      className={cn(controlBase, "pr-8", invalid ? "border-stroke-error" : "border-stroke-strong", className)}
    >
      {children}
    </select>
  );
}

export function TextArea({
  invalid,
  className,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <textarea
      {...props}
      aria-invalid={invalid || undefined}
      className={cn(
        controlBase,
        "min-h-[76px] resize-y py-2 leading-relaxed",
        invalid ? "border-stroke-error" : "border-stroke-strong",
        className,
      )}
    />
  );
}

/**
 * Quantity stepper. A cashier changes qty far more often than anything else on a bill, so it gets
 * dedicated ± targets rather than a text field — typing a number to change 1→2 is the wrong shape
 * for a counter.
 */
export function NumberStepper({
  value,
  onChange,
  min = 0,
  max = 99,
  label = "Quantity",
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  label?: string;
}) {
  const step = (delta: number) => onChange(Math.min(max, Math.max(min, value + delta)));
  return (
    <div className="inline-flex min-h-target-dense items-stretch overflow-hidden rounded-control border border-stroke-strong">
      <button
        type="button"
        onClick={() => step(-1)}
        disabled={value <= min}
        aria-label={`Decrease ${label.toLowerCase()}`}
        className="grid w-10 place-items-center bg-bg-base text-base font-semibold text-text-weak transition-colors duration-150 ease-counter hover:bg-fill-neutral-weak disabled:opacity-40"
      >
        −
      </button>
      <output
        aria-label={label}
        className="grid min-w-[46px] place-items-center border-x border-stroke-weak bg-bg-base font-ledger text-base font-bold text-text-strong"
      >
        {value}
      </output>
      <button
        type="button"
        onClick={() => step(1)}
        disabled={value >= max}
        aria-label={`Increase ${label.toLowerCase()}`}
        className="grid w-10 place-items-center bg-bg-base text-base font-semibold text-text-weak transition-colors duration-150 ease-counter hover:bg-fill-neutral-weak disabled:opacity-40"
      >
        +
      </button>
    </div>
  );
}

/** Multi-select chips — modifiers, tags, filters. Selection is a stamp, not a checkbox. */
export function ChipToggle({
  options,
  selected,
  onToggle,
}: {
  options: { id: string; label: string; sub?: string }[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = selected.includes(o.id);
        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(o.id)}
            className={cn(
              "min-h-target-dense rounded-control border px-3 text-base font-semibold transition-colors duration-150 ease-counter",
              on
                ? "border-stroke-weak bg-fill-accent-weak text-honey-800"
                : "border-stroke-weak bg-bg-base text-text-weak hover:border-stroke-strong hover:text-text-strong",
            )}
          >
            {o.label}
            {o.sub && <span className="ml-1.5 font-ledger text-sm text-text-disabled">{o.sub}</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Single-select chips — one answer out of a few, where tapping another replaces it. Read by a screen
 * reader as a radio group, which is what it is; {@link ChipToggle} is for any number of picks.
 */
export function RadioChips({
  options,
  value,
  onChange,
  label,
}: {
  options: { id: string; label: string; sub?: string }[];
  value: string | null;
  onChange: (id: string) => void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.id)}
            className={cn(
              "min-h-target-dense rounded-control border px-3 text-base font-semibold transition-colors duration-150 ease-counter",
              on
                ? "border-stroke-weak bg-fill-accent-weak text-honey-800"
                : "border-stroke-weak bg-bg-base text-text-weak hover:border-stroke-strong hover:text-text-strong",
            )}
          >
            {o.label}
            {o.sub && <span className="ml-1.5 font-ledger text-sm text-text-disabled">{o.sub}</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * On/off switch. Every settings-shaped screen needs one, and each hand-rolled copy drifted:
 * the first pair used Tailwind fractional spacing that does not exist (`w-10.5`, `top-0.75`)
 * and rendered as an unstyled blob. The knob geometry is pinned in arbitrary px here once.
 *
 * `label` is the accessible name and is always required; pass `hideLabel` when the row around
 * the switch already reads as its label.
 */
export function Switch({
  checked,
  onChange,
  label,
  description,
  hideLabel,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: string;
  hideLabel?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <label
      className={cn(
        "flex min-h-target-dense items-center gap-3",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        className,
      )}
    >
      {hideLabel ? (
        <span className="sr-only">{label}</span>
      ) : (
        <span className="flex-1">
          <b className="block text-base font-bold text-text-strong">{label}</b>
          {description && (
            <span className="block text-sm font-semibold text-text-weak">{description}</span>
          )}
        </span>
      )}
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className={cn(
          "relative h-[26px] w-[46px] flex-none rounded-full transition-colors duration-150 ease-counter peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-stroke-focus",
          checked ? "bg-fill-brand" : "border border-stroke-strong bg-fill-neutral-weak",
        )}
      >
        <span
          className={cn(
            "absolute top-[3px] h-[20px] w-[20px] rounded-full bg-bg-base transition-transform duration-150 ease-counter",
            checked ? "translate-x-[23px]" : "translate-x-[3px]",
          )}
        />
      </span>
    </label>
  );
}

/** Primary / secondary action buttons, so every screen stops hand-rolling them. */
export function ActionButton({
  variant = "secondary",
  busy,
  className,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "danger";
  busy?: boolean;
}) {
  return (
    <button
      {...props}
      disabled={props.disabled || busy}
      className={cn(
        "min-h-target-dense rounded-control px-3.5 text-base font-semibold transition-colors duration-150 ease-counter disabled:opacity-60",
        variant === "primary" && "bg-fill-brand text-text-on-fill hover:bg-fill-brand-hover",
        variant === "danger" && "bg-fill-error text-text-on-fill hover:bg-alert-700",
        variant === "secondary" &&
          "border border-stroke-strong bg-bg-base text-text-strong hover:bg-fill-neutral-weak",
        className,
      )}
    >
      {busy ? <WorkingLabel /> : children}
    </button>
  );
}
